// React Query
import {
  UseMutationResult,
  UseQueryResult,
  QueryClient,
} from '@tanstack/react-query';
// Http Methods
import { getRequest as get, postRequest as post } from '../utils/HttpMethods';

// Interfaces
import {
  Patient,
  SearchPatientResult,
  isPatient,
  isSearchPatientResult,
} from 'interfaces';

//Utils
import queryString from '../utils/queryStringBuilder';
import {
  PaginatedResult,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
  usePaginatedQuery,
  usePreFetchQuery,
} from '../utils/queries';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import { basePatientSchema } from '@billing/validation/basePatient';
import {
  convertFiltersToQueryString,
  convertGlobalFilterToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';

export const patientQueryKeys = {
  all: ['patients'],
  list: () => [...patientQueryKeys.all, 'list'],
  listPatientNames: () => [...patientQueryKeys.all, 'list-names'],
  get: (id: string) => ['patient', id],
  // Paginated patients by organization (mirrors claims pagination key style)
  paginatedPatientsByOrganization: (
    organizationID: string,
    page: number,
    pageSize: number,
    filters?: MRT_ColumnFiltersState,
    globalFilter?: string,
    sorting?: MRT_SortingState
  ) => [
    ...patientQueryKeys.all,
    'organization',
    organizationID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
};

/**
 * @description Creates a new patient.
 * @param {Patient} patient - The patient data to create.
 * @returns {Promise<Patient>} A promise that resolves to the created patient.
 */
const createPatient = async (
  patient: Patient
): Promise<{ error: string | null; data: Patient | null }> => {
  return post<{ error: string | null; data: Patient | null }>(
    'QBilling',
    `/resource/patient`,
    patient
  );
};

/**
 * @description Updates an existing patient.
 * @param {Patient} patient - The patient data to update.
 * @returns {Promise<Patient>} A promise that resolves to the updated patient.
 */
const updatePatient = async (patient: Patient): Promise<Patient> => {
  return updateEntity<Patient>({
    entity: patient,
    functionName: 'updatePatient',
    isEntity: isPatient,
    path: '/resource/patient',
  });
};

/**
 * @description Searches the patients based on firstName, lastName, and/or phin
 * @param {string} searchValue - The patient data to search on. Can be either firstName, lastName, or phin
 * @returns {Promise<SearchPatientResult[]>} A promise that resolves to an array of possible matches for the search.
 */
const searchPatients = async (
  searchValue: string,
  organizationID?: string
): Promise<SearchPatientResult[]> => {
  return get<SearchPatientResult[]>(
    'QBilling',
    `/resource/patient/search${queryString({ searchValue, organizationID })}`
  );
};

/**
 * @description Hook to fetch a patient by ID from the server.
 * @param {Object} params - The parameters for the query.
 * @param {string} params.id - The ID of the patient to fetch.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<SearchPatientResult, Error>} The result of the query.
 */
export const useSearchPatients = ({
  searchValue,
  organizationID,
  displayNotification = false,
}: {
  searchValue: string;
  organizationID: string;
  displayNotification?: boolean;
}): UseQueryResult<SearchPatientResult[], Error> => {
  return useListQuery<SearchPatientResult>({
    queryKey: patientQueryKeys.get(searchValue),
    fetchFunction: () => searchPatients(searchValue, organizationID),
    functionName: 'searchPatients',
    params: searchValue,
    isEntity: isSearchPatientResult,
    enabled: !!searchValue,
    displayNotification,
  });
};

/**
 * @description Fetches the list of patients from the server.
 * @returns {Promise<Patient[]>} A promise that resolves to an array of patients.
 */
const listPatients = async (): Promise<Patient[]> =>
  await get<Patient[]>('QBilling', `/resource/patient/all`);

/**
 * @description Hook to fetch the list of patients from the server.
 * @returns {UseQueryResult<Patient[], Error>} The result of the query.
 */
export const useListPatients = (): UseQueryResult<Patient[], Error> => {
  return useListQuery<Patient>({
    queryKey: patientQueryKeys.list(),
    fetchFunction: listPatients,
    isEntity: isPatient,
    functionName: 'listPatients',
    enabled: true,
  });
};

export const usePaginatePatientsByOrganization = ({
  organizationID,
  page = 0,
  pageSize = 25,
  columnFilters,
  globalFilter,
  sorting,
}: {
  organizationID: string;
  page?: number;
  pageSize?: number;
  columnFilters?: MRT_ColumnFiltersState;
  globalFilter?: string;
  sorting?: MRT_SortingState;
}): UseQueryResult<PaginatedResult<Patient>, Error> => {
  const enabled = !!organizationID;

  return usePaginatedQuery<Patient>({
    queryKey: patientQueryKeys.paginatedPatientsByOrganization(
      organizationID,
      page,
      pageSize,
      columnFilters,
      globalFilter,
      sorting
    ),
    isEntity: isPatient,
    functionName: 'paginatePatientsByOrganization',
    params: {
      organizationID,
      page,
      pageSize,
      filters: columnFilters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatePatientByOrganization,
    enabled,
    validationSchema: basePatientSchema,
  });
};

const paginatePatientByOrganization = async ({
  organizationID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: {
  organizationID: string;
  page: number;
  pageSize: number;
  filters?: MRT_ColumnFiltersState;
  globalFilter?: string;
  sorting?: MRT_SortingState;
}): Promise<PaginatedResult<Patient> | null> => {
  const filterString = convertFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);
  const patients = await get<PaginatedResult<Patient>>(
    'QBilling',
    `/resource/patient/organization${queryString({
      organizationID,
      page,
      pageSize,
      filters: filterString,
      globalFilter: globalFilterString,
      sorting: sortingString,
    })}`
  );
  return patients;
};

// Prefetch helper mirroring claim prefetch pattern
// Prefetch paginated patients (utility, not a React hook)
export const preFetchPaginatePatientsByOrganization = async (
  queryClient: QueryClient,
  organizationID: string,
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
) => {
  const functionName = 'paginatePatientsByOrganization';
  if (!organizationID) return;

  const queryKey = patientQueryKeys.paginatedPatientsByOrganization(
    organizationID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  await usePreFetchQuery<Patient>({
    queryClient,
    queryKey,
    isEntity: isPatient,
    functionName,
    params: {
      organizationID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatePatientByOrganization,
    displayNotification: false,
    validationSchema: basePatientSchema,
  });
};

/**
 * @description Fetches a patient by ID from the server.
 * @param {string} id - The ID of the patient to fetch.
 * @returns {Promise<Patient | null>} A promise that resolves to the patient or null.
 */
const getPatient = async (id: string): Promise<Patient | null> =>
  await get<Patient>('QBilling', `/resource/patient${queryString({ id })}`);

/**
 * @description Hook to fetch a patient by ID from the server.
 * @param {Object} params - The parameters for the query.
 * @param {string} params.id - The ID of the patient to fetch.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Patient, Error>} The result of the query.
 */
export const useGetPatient = ({
  id,
  displayNotification = false,
}: {
  id: string;
  displayNotification?: boolean;
}): UseQueryResult<Patient, Error> => {
  return useGetQuery<Patient>({
    queryKey: patientQueryKeys.get(id),
    fetchFunction: getPatient,
    functionName: 'getPatient',
    params: id,
    isEntity: isPatient,
    enabled: !!id,
    displayNotification,
  });
};

/**
 * @description Hook to create a new patient.
 * @returns {UseMutationResult<Patient, Error>} The result of the mutation.
 */
export const useCreatePatient = (): UseMutationResult<
  { error: string | null; data: Patient | null },
  Error
> => {
  return useMutationQuery<{ error: string | null; data: Patient | null }>({
    mutationFn: createPatient,
    errorMessage: 'There was an error creating the patient',
    successMessage: 'Patient created successfully',
    queryKey: patientQueryKeys.all,
    displayNotification: false,
  });
};

/**
 * @description Hook to update an existing patient.
 * @returns {UseMutationResult<Patient, Error>} The result of the mutation.
 */
export const useUpdatePatient = (): UseMutationResult<Patient, Error> => {
  return useMutationQuery<Patient>({
    mutationFn: updatePatient,
    errorMessage: 'There was an error updating the patient',
    successMessage: 'Patient updated successfully',
    queryKey: patientQueryKeys.all,
  });
};
